import type { CSSProperties, SyntheticEvent } from 'react';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ImageProps } from './Image.type';

const BLUR_FADE_DURATION = 300;
const BLUR_FILTER = 'blur(2px)';

/**
 * Image component
 *
 * @example
 * // Basic usage
 * <Image src="/profile.png" alt="Profile" width={500} height={500} />
 *
 * @example
 * // With blur placeholder
 * <Image
 *   src="/hero.jpg"
 *   alt="Hero"
 *   width={1200}
 *   height={600}
 *   placeholder="blur"
 *   blurDataURL="data:image/jpeg;base64,..."
 * />
 *
 * @example
 * // Fill mode (parent must have position: relative)
 * <div style={{ position: 'relative', width: '100%', height: '400px' }}>
 *   <Image src="/background.jpg" alt="Background" fill style={{ objectFit: 'cover' }} />
 * </div>
 */
export function Image(imageProps: ImageProps) {
  const {
    src,
    alt,
    width,
    height,
    fill = false,
    loader,
    sizes,
    quality = 100,
    preload = false,
    placeholder = 'empty',
    style,
    onLoad,
    onError,
    loading = 'lazy',
    blurDataURL,
    unoptimized = false,
    decoding = 'async',
    ...props
  } = imageProps;

  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [showBlur, setShowBlur] = useState<boolean>(true);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Generate the image source using loader if provided
  const imageSrc = useMemo<string>(() => {
    if (!src) return '';
    if (unoptimized || !loader) return src;

    return loader({
      src,
      width: width || 0,
      quality,
    });
  }, [src, loader, width, quality, unoptimized]);

  // Get blur data URL
  const blurSrc = useMemo<string | undefined>(() => {
    if (placeholder === 'blur' && blurDataURL) {
      return blurDataURL;
    }
    if (placeholder?.startsWith('data:image')) {
      return placeholder;
    }
    return undefined;
  }, [blurDataURL, placeholder]);

  // Should show blur placeholder
  const hasBlurPlaceholder = placeholder === 'blur' || placeholder?.startsWith('data:image');

  // Handle image load
  const handleLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      setIsLoaded(true);

      // Fade out blur after transition
      if (hasBlurPlaceholder && blurSrc) {
        blurTimerRef.current = setTimeout(() => {
          setShowBlur(false);
        }, BLUR_FADE_DURATION);
      }

      onLoad?.(event);
    },
    [onLoad, hasBlurPlaceholder, blurSrc]
  );

  // Handle image error
  const handleError = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      onError?.(event);
    },
    [onError]
  );

  // Preload image if requested
  useEffect(() => {
    if (preload && imageSrc) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = imageSrc;
      if (sizes) link.setAttribute('imagesizes', sizes);
      document.head.appendChild(link);

      return () => {
        document.head.removeChild(link);
      };
    }
  }, [preload, imageSrc, sizes]);

  // Reset states when src changes
  useEffect(() => {
    setIsLoaded(false);
    setShowBlur(true);

    return () => {
      if (blurTimerRef.current) {
        clearTimeout(blurTimerRef.current);
      }
    };
  }, [src]);

  // Image styles for fill mode
  const imgStyle = useMemo<CSSProperties>(() => {
    const baseStyle: CSSProperties = {
      color: 'transparent', // Hide alt text while loading
      ...style,
    };

    if (fill) {
      baseStyle.position = 'absolute';
      baseStyle.height = '100%';
      baseStyle.width = '100%';
      baseStyle.objectFit = style?.objectFit || 'contain';
      baseStyle.inset = 0;
    }

    return baseStyle;
  }, [fill, style]);

  // Blur placeholder styles
  const blurStyle = useMemo<CSSProperties>(() => {
    const baseStyle: CSSProperties = {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: style?.objectFit || 'contain',
      objectPosition: style?.objectPosition || 'center',
      filter: BLUR_FILTER,
      opacity: isLoaded ? 0 : 1,
      transition: `opacity ${BLUR_FADE_DURATION}ms ease-out`,
      pointerEvents: 'none',
    };

    return baseStyle;
  }, [isLoaded, style?.objectFit, style?.objectPosition]);

  // Wrapper styles for blur placeholder
  const wrapperStyle = useMemo<CSSProperties>(() => {
    const baseStyle: CSSProperties = {
      overflow: 'hidden',
    };

    if (fill) {
      baseStyle.position = 'absolute';
      baseStyle.inset = 0;
      baseStyle.width = '100%';
      baseStyle.height = '100%';
      baseStyle.objectFit = style?.objectFit || 'contain';
    } else {
      baseStyle.position = 'relative';
      baseStyle.display = 'inline-block';
      if (width) baseStyle.width = width;
      if (height) baseStyle.height = height;
    }

    return baseStyle;
  }, [fill, width, height]);

  // If we have a blur placeholder, wrap the image
  if (hasBlurPlaceholder && blurSrc) {
    return (
      <span style={wrapperStyle}>
        {/* Blur placeholder */}
        {showBlur && <img src={blurSrc} alt={''} aria-hidden={'true'} style={blurStyle} />}
        {/* Main image */}
        <img
          src={imageSrc}
          alt={alt}
          width={!fill ? width : undefined}
          height={!fill ? height : undefined}
          sizes={sizes}
          loading={loading}
          decoding={decoding}
          style={imgStyle}
          onLoad={handleLoad}
          onError={handleError}
          {...props}
        />
      </span>
    );
  }

  // Without blur placeholder, render just the image
  return (
    <img
      src={imageSrc}
      alt={alt}
      width={!fill ? width : undefined}
      height={!fill ? height : undefined}
      sizes={sizes}
      loading={loading}
      decoding={decoding}
      style={imgStyle}
      onLoad={handleLoad}
      onError={handleError}
      {...props}
    />
  );
}
